import type { DispatchColor, DispatchColumn } from "@storyboard/dispatch-view/view";
import { brandOther, brandPrimary, zinc } from "@/ui/tokens";

/** The dark-theme paint for each named view color; bright enough to scan against the zinc rows. */
export const DISPATCH_COLOR_VALUES: Record<DispatchColor, string> = {
  purple: "#c084fc",
  blue: "#60a5fa",
  teal: "#2dd4bf",
  green: "#4ade80",
  amber: "#fbbf24",
  orange: "#fb923c",
  red: "#f87171",
  zinc: zinc[400],
};

/** The paint of everything about a task: the chip, its History entries. The view palette's purple, never the dot's red. */
export const DISPATCH_TASK = DISPATCH_COLOR_VALUES.purple;

/** The paint a value in a column takes: its `valueColors` entry, else the column's `color`, else none. */
export function dispatchColorFor(column: DispatchColumn, value: string): string | undefined {
  const named = column.valueColors?.[value] ?? column.color;
  return named ? DISPATCH_COLOR_VALUES[named] : undefined;
}

/**
 * The keyboard's row and the detail beneath it, painted as one block: the same tint on both and one bar down their
 * left edge. `selected` is the highlight at rest, in the brand's soft slate teal; `composing` is the highlight while
 * the inline reply has the cursor, in the brand purple. Every value is opaque, since frozen cells paint over the rows
 * scrolling behind them.
 */
export const DISPATCH_HIGHLIGHT = {
  selected: {
    tint: brandOther[600],
    hover: `color-mix(in oklch, ${brandOther[600]} 75%, ${brandOther[500]})`,
    bar: brandOther[300],
  },
  composing: { tint: brandPrimary[800], hover: brandPrimary[800], bar: brandPrimary[400] },
} as const;

/** The brand tint behind everything that says rows are hidden: the banner, a filtered header, the Filter button. */
export const FILTERED_TINT = `color-mix(in oklch, ${brandPrimary[600]} 18%, transparent)`;

/** The brand-outline look every filtered-state action shares: the Filter button, "Clear all filters", "Clear filters". */
export const FILTERED_ACTION_SX = {
  color: brandPrimary[200],
  border: `1px solid ${brandPrimary[400]}`,
  "&:hover": { bgcolor: `color-mix(in oklch, ${brandPrimary[600]} 30%, transparent)`, color: "white" },
} as const;
